import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Progress, Select, Table } from 'antd';
import { ButtonTheme } from '../../../../components';
import { ContainerButton } from '../../../../styles/global-style';
import { LINE_MESSAGE_PLACEHOLDERS, renderClaimMessage } from '../../shared/lineMessage';
import { useLineMessageTemplates } from '../hooks/useLineMessageTemplates';
import { useBulkSendLineMessage } from '../hooks/useBulkSendLineMessage';

const EMPTY_TEMPLATE_VALUE = '__EMPTY__';
const MESSAGE_MAX_LENGTH = 5000;
const PREVIEW_COUNT = 3;

const STATUS_LABEL_TH = {
  SENT: 'ส่งสำเร็จ',
  SKIPPED_NO_LINE: 'ข้ามเพราะไม่มี LINE',
  FAILED: 'ส่งไม่สำเร็จ',
};

const PlaceholderLegend = ({ onInsert }) => (
  <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 12 }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>แทรกข้อมูลอัตโนมัติ (คลิกเพื่อแทรก)</div>
    {LINE_MESSAGE_PLACEHOLDERS.map((placeholder) => (
      <div key={placeholder.token} style={{ marginBottom: 6 }}>
        <a onClick={() => onInsert(placeholder.token)}>{placeholder.token}</a>
        <div style={{ color: '#999', fontSize: 12 }}>
          {placeholder.description} เช่น {placeholder.example}
        </div>
      </div>
    ))}
  </div>
);

export const BulkSendLineMessage = ({ open, onToggle, modal, selectedClaims = [] }) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(undefined);
  const [message, setMessage] = useState('');
  const textAreaRef = useRef(null);

  const { data: templates = [], isPending: isPendingTemplates } = useLineMessageTemplates(open);
  const { send, reset, isSending, sentCount, totalCount, summary } = useBulkSendLineMessage();

  useEffect(() => {
    if (open) {
      setSelectedTemplateId(undefined);
      setMessage('');
      reset();
    }
  }, [open]);

  const onSelectTemplate = (value) => {
    setSelectedTemplateId(value);
    if (value === EMPTY_TEMPLATE_VALUE) {
      setMessage('');
      return;
    }
    const template = templates.find((item) => item.templateId === value);
    setMessage(template?.templateText || '');
  };

  // แทรก token ที่ตำแหน่ง cursor ปัจจุบันของ textarea ไม่ใช่ต่อท้ายข้อความเสมอ
  const insertToken = (token) => {
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    if (!textarea) {
      setMessage((prev) => `${prev}${token}`);
      return;
    }
    const start = textarea.selectionStart ?? message.length;
    const end = textarea.selectionEnd ?? message.length;
    const next = `${message.slice(0, start)}${token}${message.slice(end)}`;
    setMessage(next);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const isMessageValid = message.trim().length > 0 && message.length <= MESSAGE_MAX_LENGTH;

  const onClose = () => {
    if (isSending) return;
    onToggle({ defaultValues: '' });
  };

  const onClickSend = () => {
    modal.confirm({
      title: `ยืนยันส่งข้อความถึง ${selectedClaims.length} เคส?`,
      okText: 'ยืนยัน',
      cancelText: 'ยกเลิก',
      onOk: () => send(selectedClaims, message),
    });
  };

  const failedItems = (summary?.items || []).filter((item) => item.status !== 'SENT');

  return (
    <Modal
      title={`ส่งข้อความ LINE (${selectedClaims.length} รายการ)`}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      closable={!isSending}
      footer={null}
      destroyOnClose
      width={760}
    >
      {!summary && (
        <>
          <div style={{ marginBottom: 8 }}>เลือก template (หรือเริ่มจากข้อความว่าง)</div>
          <Select
            style={{ width: '100%' }}
            placeholder="เลือก template"
            showSearch
            loading={isPendingTemplates}
            value={selectedTemplateId}
            onChange={onSelectTemplate}
            optionFilterProp="label"
            disabled={isSending}
            options={[
              { value: EMPTY_TEMPLATE_VALUE, label: 'เริ่มจากข้อความว่าง' },
              ...templates.map((template) => ({
                value: template.templateId,
                label: template.templateName,
              })),
            ]}
          />

          <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ marginBottom: 8 }}>
                ข้อความ (ระบุได้ไม่เกิน {MESSAGE_MAX_LENGTH} ตัวอักษร)
              </div>
              <Input.TextArea
                ref={textAreaRef}
                rows={6}
                maxLength={MESSAGE_MAX_LENGTH}
                showCount
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSending}
              />

              <div style={{ marginTop: 16, fontWeight: 600 }}>ตัวอย่างข้อความที่ลูกค้าจะได้รับ</div>
              {selectedClaims.slice(0, PREVIEW_COUNT).map((claim) => (
                <div
                  key={claim.claimId}
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: '#fafafa',
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#999' }}>{claim.claimCode}</div>
                  {renderClaimMessage(message, claim)}
                </div>
              ))}
            </div>
            <div style={{ width: 260, flexShrink: 0 }}>
              <PlaceholderLegend onInsert={insertToken} />
            </div>
          </div>

          {isSending && (
            <div style={{ marginTop: 16 }}>
              <Progress percent={Math.round((sentCount / (totalCount || 1)) * 100)} />
              <div>{`ส่งแล้ว ${sentCount} / ${totalCount}`}</div>
            </div>
          )}

          <ContainerButton right style={{ marginTop: 16 }}>
            <ButtonTheme
              useFor="LINE2"
              title="ส่ง"
              onClick={onClickSend}
              disabled={!isMessageValid || isSending}
              loading={isSending}
            />
            <ButtonTheme useFor="CANCEL" onClick={onClose} disabled={isSending} />
          </ContainerButton>
        </>
      )}

      {summary && (
        <div>
          <Alert
            type="info"
            showIcon
            message={`ส่งสำเร็จ ${summary.sent} / ข้ามเพราะไม่มี LINE ${summary.skipped} / ส่งไม่สำเร็จ ${summary.failed}`}
          />
          {failedItems.length > 0 && (
            <Table
              style={{ marginTop: 16 }}
              size="small"
              pagination={false}
              rowKey={(record, index) => record.claimId ?? index}
              dataSource={failedItems}
              columns={[
                { title: 'เลขที่เคลม', dataIndex: 'claimCode' },
                {
                  title: 'สถานะ',
                  dataIndex: 'status',
                  render: (status) => STATUS_LABEL_TH[status] || status,
                },
                { title: 'เหตุผล', dataIndex: 'reason' },
              ]}
            />
          )}
          <ContainerButton right style={{ marginTop: 16 }}>
            <Button onClick={onClose}>ปิด</Button>
          </ContainerButton>
        </div>
      )}
    </Modal>
  );
};
